import type { OrganizationMember } from '@reach/shared-types/workos-auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { type ApiError, partnerApi } from '../../lib/api';
import { ConfirmDialog } from './team/ConfirmDialog';
import { InviteDialog } from './team/InviteDialog';
import { MembersTable } from './team/MembersTable';

const getErrorMessage = (error: unknown): string => {
  const apiError = error as ApiError;
  if (apiError.response?.data?.message) {
    return apiError.response.data.message;
  }
  if (apiError.response?.data?.error) {
    return apiError.response.data.error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Unknown error';
};

export function TeamPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<OrganizationMember | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<OrganizationMember | null>(null);

  const [membersCursor, setMembersCursor] = useState<{
    before?: string;
    after?: string;
  }>({});

  const {
    data: membersData,
    isLoading: isLoadingMembers,
    isFetching: isFetchingMembers,
  } = useQuery({
    queryKey: ['team-members', membersCursor],
    queryFn: () => partnerApi.getTeamMembers({ limit: 20, ...membersCursor }),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const { data: invitationsData, isLoading: isLoadingInvitations } = useQuery({
    queryKey: ['team-invitations'],
    queryFn: () => partnerApi.getTeamInvitations({ limit: 100 }),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const members = membersData?.results ?? [];
  const membersPagination = membersData?.pagination;
  const invitations = invitationsData?.results ?? [];

  const sendInviteMutation = useMutation({
    mutationFn: (email: string) => partnerApi.sendTeamInvitation(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations'] });
      addToast({
        variant: 'success',
        title: 'Invitation sent',
        description: 'The invitation email has been sent.',
      });
      setShowInviteDialog(false);
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to send invitation',
        description: getErrorMessage(error),
      });
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (invitationId: string) => partnerApi.revokeTeamInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations'] });
      addToast({
        variant: 'success',
        title: 'Invitation revoked',
      });
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to revoke invitation',
        description: getErrorMessage(error),
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (membershipId: string) => partnerApi.deactivateTeamMember(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      addToast({
        variant: 'success',
        title: 'Member deactivated',
      });
      setDeactivateTarget(null);
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to deactivate member',
        description: getErrorMessage(error),
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (membershipId: string) => partnerApi.reactivateTeamMember(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      addToast({
        variant: 'success',
        title: 'Member reactivated',
      });
      setReactivateTarget(null);
    },
    onError: error => {
      addToast({
        variant: 'destructive',
        title: 'Failed to reactivate member',
        description: getErrorMessage(error),
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        description="Manage your organization's team members and invitations."
        right={
          <Button size="sm" onClick={() => setShowInviteDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        }
      />

      <MembersTable
        members={members}
        invitations={invitations}
        isLoadingMembers={isLoadingMembers}
        isLoadingInvitations={isLoadingInvitations}
        isFetchingMembers={isFetchingMembers}
        isRevokingInvitation={revokeInviteMutation.isPending}
        currentUserId={user?.id}
        pagination={membersPagination}
        onNextPage={() => {
          if (membersPagination?.cursor) {
            setMembersCursor({ after: membersPagination.cursor });
          }
        }}
        onPreviousPage={() => {
          if (membersPagination?.previousCursor) {
            setMembersCursor({
              before: membersPagination.previousCursor,
            });
          }
        }}
        onDeactivate={setDeactivateTarget}
        onReactivate={setReactivateTarget}
        onRevokeInvitation={id => revokeInviteMutation.mutate(id)}
      />

      <InviteDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSubmit={email => sendInviteMutation.mutate(email)}
        isPending={sendInviteMutation.isPending}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => {
          if (deactivateTarget) {
            deactivateMutation.mutate(deactivateTarget.id);
          }
        }}
        isPending={deactivateMutation.isPending}
        title="Deactivate Member"
        description={`Are you sure you want to deactivate ${deactivateTarget?.user.email}? They will no longer be able to access this organization.`}
        confirmLabel="Deactivate"
        variant="destructive"
      />

      <ConfirmDialog
        open={!!reactivateTarget}
        onClose={() => setReactivateTarget(null)}
        onConfirm={() => {
          if (reactivateTarget) {
            reactivateMutation.mutate(reactivateTarget.id);
          }
        }}
        isPending={reactivateMutation.isPending}
        title="Reactivate Member"
        description={`Are you sure you want to reactivate ${reactivateTarget?.user.email}? They will regain access to this organization.`}
        confirmLabel="Reactivate"
        variant="default"
      />
    </div>
  );
}
